import { useMemo } from "react";
import type { StepInfo } from "../bookingForm/BookingStepHeader";
import type { BookingOptions, Availability } from "../../../common/jsonRpcTypes";

interface UseBookingStepsParams {
  selectedTypeId: string | null | undefined;
  bookingOptions: BookingOptions | null;
  availability: Availability | null;
}

interface UseBookingStepsResult {
  bookingSteps: StepInfo[];
}

/**
 * useBookingSteps — computes the list of steps in the booking journey
 * based on the selected booking type's preorder availability and deposit
 * requirements.
 *
 * Extracted from BookingForm to keep its cognitive complexity low.
 */
export function useBookingSteps({
  selectedTypeId,
  bookingOptions,
  availability,
}: UseBookingStepsParams): UseBookingStepsResult {
  const bookingSteps: StepInfo[] = useMemo(() => {
    const preorders = bookingOptions?.preorders;
    const typeMenus = selectedTypeId && preorders ? preorders[selectedTypeId] : undefined;
    const hasPreorders = !!(typeMenus && typeMenus.length > 0);
    const hasDeposit = !!bookingOptions?.depositRequired && availability?.action === "accept";

    const steps: StepInfo[] = [{ name: "Details", stage: "details" }];
    if (hasPreorders) {
      steps.push({ name: "Pre-order", stage: "preorders" });
    }
    if (hasDeposit || hasPreorders) {
      steps.push({ name: "Payment", stage: "payment" });
    }
    return steps;
  }, [selectedTypeId, bookingOptions?.preorders, bookingOptions?.depositRequired, availability?.action]);

  return { bookingSteps };
}

/**
 * Determine which stages should show step-related UI elements.
 */
export function getStageVisibility(stage: string): {
  showStepHeader: boolean;
  showInlineBookingInfo: boolean;
} {
  const showStepHeader = stage !== "initial" && stage !== "complete" && stage !== "complete-redirect";
  const showInlineBookingInfo =
    stage !== "initial" && stage !== "details" && stage !== "complete" && stage !== "complete-redirect";
  return { showStepHeader, showInlineBookingInfo };
}

/**
 * Derive step indicator info from the booking steps and current stage.
 */
export function getStepIndicatorInfo(
  bookingSteps: StepInfo[],
  stage: string,
): {
  currentStepIndex: number;
  currentStepInfo: StepInfo | undefined;
  currentStepNumber: number;
  totalStepCount: number;
} {
  const currentStepIndex = bookingSteps.findIndex((s) => s.stage === stage);
  const currentStepInfo = currentStepIndex >= 0 ? bookingSteps[currentStepIndex] : undefined;
  const currentStepNumber = currentStepIndex + 1;
  const totalStepCount = bookingSteps.length;
  return { currentStepIndex, currentStepInfo, currentStepNumber, totalStepCount };
}
